import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BadgeEuro, CheckCircle2 } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { estimatePreSignupAidPotential } from '../lib/preSignupAidEstimate';

const steps = [
  {
    key: 'age',
    label: 'Quel age as-tu ?',
    type: 'number',
    placeholder: 'Ex: 24',
  },
  {
    key: 'status',
    label: 'Quelle est ta situation principale ?',
    type: 'choice',
    options: [
      ['student', 'Etudiant'],
      ['employee', 'Salarie'],
      ['freelance', 'Freelance / independant'],
      ['unemployed', 'Sans emploi'],
    ],
  },
  {
    key: 'housing',
    label: 'Ton logement actuel ?',
    type: 'choice',
    options: [
      ['renter', 'Locataire'],
      ['owner', 'Proprietaire'],
      ['hosted', 'Heberge'],
      ['student_residence', 'Residence etudiante'],
    ],
  },
  {
    key: 'monthlyIncome',
    label: 'Tes revenus mensuels nets environ ?',
    type: 'number',
    placeholder: 'Ex: 950',
  },
  {
    key: 'familySituation',
    label: 'Ta situation familiale ?',
    type: 'choice',
    options: [
      ['single', 'Seul(e)'],
      ['couple', 'En couple'],
      ['single_parent', 'Parent solo'],
    ],
  },
  {
    key: 'children',
    label: "Combien d'enfants a charge ?",
    type: 'number',
    placeholder: 'Ex: 0',
  },
  {
    key: 'city',
    label: 'Dans quelle ville habites-tu ?',
    type: 'text',
    placeholder: 'Ex: Lille',
  },
];

export const AidQuizPage = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({
    age: '',
    status: '',
    housing: '',
    monthlyIncome: '',
    familySituation: '',
    children: '0',
    city: '',
  });

  const showResult = stepIndex >= steps.length;
  const currentStep = showResult ? null : steps[stepIndex];
  const isLastQuestion = stepIndex === steps.length - 1;
  const hasAnswered = showResult ? true : String(answers[currentStep.key] ?? '').trim().length > 0;
  const result = useMemo(() => estimatePreSignupAidPotential(answers), [answers]);

  const next = () => {
    if (!hasAnswered) return;
    setStepIndex((value) => value + 1);
  };

  const createAccount = () => {
    sessionStorage.setItem('skipya_aid_quiz_result', JSON.stringify({ answers, result }));
    navigate('/register?source=aid-quiz');
  };

  return (
    <div className={`min-h-screen px-5 py-8 ${isDark ? 'bg-[#0F0F1A] text-white' : 'bg-[#F8F7F4] text-[#1d1d1f]'}`}>
      <div className="max-w-3xl mx-auto">
        <Link to="/" className={`inline-flex items-center gap-2 mb-8 text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>
          <ArrowLeft size={17} />
          Retour
        </Link>

        <div className={`rounded-3xl border p-6 md:p-9 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200 shadow-xl'}`}>
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-[#81d8d0]/15 flex items-center justify-center">
              <BadgeEuro className="text-[#81d8d0]" size={25} />
            </div>
            <div>
              <p className="text-[#81d8d0] text-sm font-semibold">Quiz aides gratuit</p>
              <p className={`text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>
                7 questions, estimation indicative avant inscription
              </p>
            </div>
          </div>

          {!showResult ? (
            <motion.div key={currentStep.key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
              <div className="mb-6">
                <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
                  <div
                    className="h-full bg-[#81d8d0] transition-all duration-300"
                    style={{ width: `${((stepIndex + 1) / steps.length) * 100}%` }}
                  />
                </div>
                <p className={`text-xs mt-2 ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>
                  Question {stepIndex + 1}/{steps.length}
                </p>
              </div>

              <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mb-7">{currentStep.label}</h1>

              {currentStep.type === 'choice' ? (
                <div className="grid sm:grid-cols-2 gap-3 mb-8">
                  {currentStep.options.map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setAnswers((prev) => ({ ...prev, [currentStep.key]: value }))}
                      className={`text-left rounded-2xl border p-4 transition-all ${
                        answers[currentStep.key] === value
                          ? 'border-[#81d8d0] bg-[#81d8d0]/12'
                          : isDark
                            ? 'border-white/10 bg-white/5 hover:border-white/20'
                            : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              ) : (
                <input
                  type={currentStep.type}
                  value={answers[currentStep.key]}
                  onChange={(event) => setAnswers((prev) => ({ ...prev, [currentStep.key]: event.target.value }))}
                  placeholder={currentStep.placeholder}
                  className={`w-full rounded-2xl border px-5 py-4 mb-8 outline-none ${
                    isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'
                  }`}
                />
              )}

              <button
                type="button"
                onClick={next}
                disabled={!hasAnswered}
                className="btn-apple btn-apple-primary disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {isLastQuestion ? 'Voir mon estimation' : 'Continuer'}
                <ArrowRight size={18} className="ml-2" />
              </button>
            </motion.div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
              <CheckCircle2 size={42} className="text-[#81d8d0] mb-5" />
              <h1 className="text-3xl md:text-5xl font-semibold tracking-tight mb-4">
                Tu pourrais peut-etre recuperer jusqu'a {result.monthlyPotential}€/mois.
              </h1>
              <p className={`text-lg leading-8 mb-6 ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>
                Cette estimation est indicative. Skipya doit verifier ton profil, tes justificatifs et les aides exactes avant de lancer les demarches.
              </p>
              {result.signals.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-8">
                  {result.signals.map((signal) => (
                    <span key={signal} className="px-3 py-2 rounded-full text-sm bg-[#81d8d0]/15 text-[#81d8d0]">
                      {signal}
                    </span>
                  ))}
                </div>
              )}
              <button type="button" onClick={createAccount} className="btn-apple btn-apple-primary">
                Creer mon compte pour voir le detail
                <ArrowRight size={18} className="ml-2" />
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AidQuizPage;
